import React from 'react';
import { ReactComponent as RegularObjectiveTypeImage } from '../../../../resources/icons/objectives/regular_obj.svg';
import { ReactComponent as ThresholdAboveObjectiveTypeImage } from '../../../../resources/icons/objectives/threshold_above_obj.svg';
import { ReactComponent as ThresholdBelowObjectiveTypeImage } from '../../../../resources/icons/objectives/threshold_below_obj.svg';
import { ReactComponent as RangeObjectiveTypeImage } from '../../../../resources/icons/objectives/range_obj.svg';


export const getDimensionalQuantitativeObjectiveTypes = (dimObjective) => {
    const items = [
        { value: 'Regular-Dimensional', label: 'Regular-Dimensional', default: dimObjective ? dimObjective.objectiveType === 'QUANTITATIVE_DIMENSIONAL' : false },
        { value: 'Threshold_Above-Dimensional', label: 'Threshold Above-Dimensional', default: dimObjective ? dimObjective.objectiveType === 'QUANTITATIVE_DIMENSIONAL_THRESHOLD' && dimObjective.startComparisonOperator === 'GREATER_THAN' : false },
        { value: 'Threshold_Below-Dimensional', label: 'Threshold Below-Dimensional', default: dimObjective ? dimObjective.objectiveType === 'QUANTITATIVE_DIMENSIONAL_THRESHOLD' && dimObjective.startComparisonOperator === 'LESS_THAN' : false },
        { value: 'Range-Dimensional', label: 'Range-Dimensional', default: dimObjective ? dimObjective.objectiveType === 'QUANTITATIVE_DIMENSIONAL_THRESHOLD' && dimObjective.startComparisonOperator === 'GREATER_THAN' && dimObjective.endComparisonOperator === 'LESS_THAN' : false },
    ];
    return items;
}

export function getDimObjectiveType(objective) {
    if (!objective) {
        return null;
    }
    if (objective.objectiveType === 'QUANTITATIVE_DIMENSIONAL') {
        return 'Regular-Dimensional';
    } else if (objective.objectiveType === 'QUANTITATIVE_DIMENSIONAL_THRESHOLD' && objective.startComparisonOperator === 'GREATER_THAN' && objective.endComparisonOperator === 'LESS_THAN') {
        return 'Range-Dimensional';
    } else if (objective.objectiveType === 'QUANTITATIVE_DIMENSIONAL_THRESHOLD' && objective.startComparisonOperator === 'GREATER_THAN') {
        return 'Threshold_Above-Dimensional';
    } else if (objective.objectiveType === 'QUANTITATIVE_DIMENSIONAL_THRESHOLD' && objective.startComparisonOperator === 'LESS_THAN') {
        return 'Threshold_Below-Dimensional';
    }
    return null;
}

export const renderSVGImage = (selectedObjectiveType) => {
    let image = null;
    switch (selectedObjectiveType) {
        case 'Regular':
        case 'Regular-Dimensional':
            image = <RegularObjectiveTypeImage />;
            break;
        case 'Threshold_Above':
        case 'Threshold_Above-Dimensional':
            image = <ThresholdAboveObjectiveTypeImage />;
            break;
        case 'Threshold_Below':
        case 'Threshold_Below-Dimensional':
            image = <ThresholdBelowObjectiveTypeImage />;
            break;
        case 'Range':
        case 'Range-Dimensional':
            image = <RangeObjectiveTypeImage />;
            break;
        default:
            image = null;
            break;
    }
    return image;
};

export function createDimensionalObjectiveFromRegularObjectives(objectives) {
    if (!objectives || objectives.length === 0) {
        return null;
    }
    const firstObjective = objectives[0];
    const dimensionalObjective = {
        objectiveType: firstObjective.objectiveType,
        startDate: firstObjective.startDate,
        endDate: firstObjective.endDate,
        applicability: firstObjective.applicability,
        valuesPerDimension: objectives.map((objective, index) => ({
            objectiveId: objective.objectiveId || 0,
            quantitativeDimensionOrder: objective.quantitativeDimensionOrder || 0,
            startQuantitativeValue: objective.startQuantitativeValue || 0,
            endQuantitativeValue: objective.endQuantitativeValue || 0,
        })),
        startComparisonOperator: firstObjective.startComparisonOperator ?? null,
        endComparisonOperator: firstObjective.endComparisonOperator ?? null,
        joinComparisonAs: firstObjective.joinComparisonAs ?? null,
        penalties: firstObjective.penalties ?? null,
        penaltiesExpressedAs: firstObjective.penaltiesExpressedAs ?? null,
    };
    return dimensionalObjective;
}

/**
 * Converts an array of regular objectives into an array of dimensional objectives.
 * Regular objectives can be grouped into dimensional objectives if they share the same start and end dates.
 * Each group of regular objectives will be transformed into a single dimensional objective.
 *
 * @param {Array} objectives - The array of regular objectives to be converted.
 * @returns {Array|null} - The array of dimensional objectives or null if the input is invalid.
 */

export function createDimensionalObjectivesFromRegularObjectives(objectives) {
    if (!objectives || objectives.length === 0) {
        return null;
    }
    // Group objectives by start and end dates
    // This creates an object where the keys are the concatenated start and end dates,
    // and the values are arrays of objectives that share those dates.
    const groupedObjectives = objectives.reduce((acc, objective) => {
        const key = `${objective.startDate}-${objective.endDate}`;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(objective);
        return acc;
    }, {});
    // Create dimensional objectives from grouped objectives
    const dimensionalObjectives = Object.values(groupedObjectives).map(group => {
        const firstObjective = group[0];
        return {
            objectiveType: firstObjective.objectiveType,
            startDate: firstObjective.startDate,
            endDate: firstObjective.endDate,
            applicability: firstObjective.applicability,
            valuesPerDimension: group.map(objective => ({
                objectiveId: objective.objectiveId ?? null,
                quantitativeDimensionOrder: objective.quantitativeDimensionOrder ?? null,
                startQuantitativeValue: objective.startQuantitativeValue ?? null,
                endQuantitativeValue: objective.endQuantitativeValue ?? null,
                samplesPerObjective: objective.samplesPerObjective ?? null,
            })),
            startComparisonOperator: firstObjective.startComparisonOperator ?? null,
            endComparisonOperator: firstObjective.endComparisonOperator ?? null,
            joinComparisonAs: firstObjective.joinComparisonAs ?? null,
            penalties: firstObjective.penalties ?? null,
            penaltiesExpressedAs: firstObjective.penaltiesExpressedAs ?? null,
        };
    });
    return dimensionalObjectives;
}

export function createRegularObjectivesFromDimensionalObjective(dimensionalObjective) {
    if (!dimensionalObjective || !dimensionalObjective.valuesPerDimension || dimensionalObjective.valuesPerDimension.length === 0) {
        return null;
    }
    const regularObjectives = dimensionalObjective.valuesPerDimension.map((dimension, index) => ({
        objectiveId: dimension.objectiveId,
        objectiveType: dimensionalObjective.objectiveType,
        startDate: dimensionalObjective.startDate,
        endDate: dimensionalObjective.endDate,
        applicability: dimensionalObjective.applicability,
        quantitativeDimensionOrder: dimension.quantitativeDimensionOrder,
        startQuantitativeValue: dimension.startQuantitativeValue,
        endQuantitativeValue: dimension.endQuantitativeValue,
        startComparisonOperator: dimensionalObjective.startComparisonOperator ?? null,
        endComparisonOperator: dimensionalObjective.endComparisonOperator ?? null,
        joinComparisonAs: dimensionalObjective.joinComparisonAs ?? null,
        penalties: dimensionalObjective.penalties ?? null,
        penaltiesExpressedAs: dimensionalObjective.penaltiesExpressedAs ?? null,
    }));
    return regularObjectives;
}

export function createRegularObjectiveFromDimensionalObjective(dimensionalObjective, dimensionOrder) {
    // console.log('dimensionalObjective:', dimensionalObjective);
    // console.log('dimensionOrder:', dimensionOrder);
    if (!dimensionalObjective || !dimensionalObjective.valuesPerDimension || dimensionalObjective.valuesPerDimension.length === 0) {
        return null;
    }
    const dimension = dimensionalObjective.valuesPerDimension.find(dimension => dimension.quantitativeDimensionOrder === dimensionOrder);
    if (!dimension) {
        return null;
    }
    const regularObjective = {
        objectiveId: dimension.objectiveId,
        objectiveType: dimensionalObjective.objectiveType,
        startDate: dimensionalObjective.startDate,
        endDate: dimensionalObjective.endDate,
        applicability: dimensionalObjective.applicability,
        quantitativeDimensionOrder: dimension.quantitativeDimensionOrder,
        startQuantitativeValue: dimension.startQuantitativeValue,
        endQuantitativeValue: dimension.endQuantitativeValue,
        startComparisonOperator: dimensionalObjective.startComparisonOperator ?? null,
        endComparisonOperator: dimensionalObjective.endComparisonOperator ?? null,
        joinComparisonAs: dimensionalObjective.joinComparisonAs ?? null,
        penalties: dimensionalObjective.penalties ?? null,
        penaltiesExpressedAs: dimensionalObjective.penaltiesExpressedAs ?? null,
    };
    // console.log('regularObjective:', regularObjective);
    return regularObjective;
}

/**
 * Compares current and reference dimension objectives and returns the changes.
 *
 * @param {Object} currentDimObjective - The current dimension objective.
 * @param {Object} referenceDimObjective - The reference dimension objective.
 * @returns {Object} An object containing arrays of added, removed, and updated dimensions.
 */

export function getDimensionChanges(currentDimObjective, referenceDimObjective) {
    const addedDimensions = currentDimObjective.valuesPerDimension.filter(dim =>
        !referenceDimObjective.valuesPerDimension.some(origDim => origDim.quantitativeDimensionOrder === dim.quantitativeDimensionOrder)
    );
    const removedDimensions = referenceDimObjective.valuesPerDimension.filter(origDim =>
        !currentDimObjective.valuesPerDimension.some(dim => dim.quantitativeDimensionOrder === origDim.quantitativeDimensionOrder)
    );
    const updatedDimensions = currentDimObjective.valuesPerDimension.filter(dim =>
        referenceDimObjective.valuesPerDimension.some(origDim =>
            origDim.quantitativeDimensionOrder === dim.quantitativeDimensionOrder &&
            (origDim.startQuantitativeValue !== dim.startQuantitativeValue || origDim.endQuantitativeValue !== dim.endQuantitativeValue)
        )
    );
    return {
        addedDimensions,
        removedDimensions,
        updatedDimensions
    };
}

/**
 * Checks if the main properties of the dimensional objective have changed.
 *
 * @param {Object} currentDimObjective - The current dimension objective.
 * @param {Object} referenceDimObjective - The reference dimension objective.
 * @returns {boolean} True if any main property has changed, false otherwise.
 */

export function hasMainDimObjectiveChanged(currentDimObjective, referenceDimObjective) {
    if (!currentDimObjective || !referenceDimObjective) {
        return false;
    }
    return currentDimObjective.startDate !== referenceDimObjective.startDate ||
        currentDimObjective.endDate !== referenceDimObjective.endDate ||
        currentDimObjective.applicability !== referenceDimObjective.applicability ||
        currentDimObjective.startComparisonOperator !== referenceDimObjective.startComparisonOperator ||
        currentDimObjective.endComparisonOperator !== referenceDimObjective.endComparisonOperator ||
        currentDimObjective.joinComparisonAs !== referenceDimObjective.joinComparisonAs ||
        currentDimObjective.penalties !== referenceDimObjective.penalties ||
        currentDimObjective.penaltiesExpressedAs !== referenceDimObjective.penaltiesExpressedAs;
}
